/**
 * REST API adapters for Site provider
 * Transforms REST responses to match GraphQL Site structure
 *
 * Usage: Replace import in provider.tsx:
 * - import { useSiteQuery } from './queries';
 * + import { useSiteQuery } from './queries.rest';
 *
 * NOTE: Some fields use hardcoded defaults as their REST endpoints
 * don't exist yet. These values match the production GraphQL responses.
 */
import type { Attribute } from '@api';
import { useMemo } from 'react';

import { OfferAttributeEnum, OfferFiltersEnum } from '@gqlTypes/globalTypes';
import { useAttributes, useSiteConfig } from '@hooks/queries';

import type { Site, Site_site } from './gqlTypes/Site';

/**
 * Default SEO values (hardcoded - no REST endpoint)
 */
const DEFAULT_SEO = {
  __typename: 'Seo' as const,
  pageTitle: 'Praca dla kierowców - oferty pracy | Praca na kołach',
  metaKeywords: 'praca kierowca, oferty pracy kierowca, praca na kołach',
  metaDescription:
    'Znajdź pracę jako kierowca. Przeglądaj oferty pracy dla kierowców wszystkich kategorii.',
};

/**
 * Default logo upload settings (hardcoded - no REST endpoint)
 */
const DEFAULT_LOGO_SETTINGS = {
  __typename: 'LogoUploadSettings' as const,
  allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  maxHeight: 1000,
  maxSize: 5242880, // 5MB
  maxWidth: 1000,
};

/**
 * Default application settings (hardcoded - no REST endpoint)
 */
const DEFAULT_APPLICATION_SETTINGS = {
  __typename: 'ApplicationSettings' as const,
  cvAllowedExtensions: ['pdf', 'doc', 'docx'],
  cvMaxSize: 10485760, // 10MB
  directionChoices: [
    ['none', 'Brak doświadczenia'],
    ['poland', 'Polska'],
    ['europe', 'Europa'],
    ['poland+abroad', 'Polska i kraje zagraniczne'],
  ],
  experienceChoices: [
    ['none', 'Brak doświadczenia'],
    ['1-2', 'Od 1 roku do 2 lat'],
    ['2-5', 'Od 2 do 5 lat'],
    ['5+', '5 lat i więcej'],
  ],
};

/**
 * Default offer settings (hardcoded - no REST endpoint)
 */
const DEFAULT_OFFER_SETTINGS = {
  __typename: 'OfferSettings' as const,
  salaryMin: 3000,
  salaryMax: 20000,
};

/**
 * Plan IDs (base64 Product:1, Product:2, Product:3) — must match backend fixture products.json
 */
const PRODUCT_IDS = {
  starter: btoa('Product:1'),
  biznes: btoa('Product:2'),
  premium: btoa('Product:3'),
};

/**
 * Default inventory: 3 plany (Starter/Biznes/Premium). Netto na karcie; brutto do zapłaty.
 * Spójne z backendem: net + vat 23% → gross (12.30, 270.60, 430.50). Preferuj GET /p24/plans/.
 */
const PLAN_SKUS = {
  starter: 'PUB-START-001',
  biznes: 'PUB-BIZ-001',
  premium: 'PUB-PREM-001',
} as const;

/** Brutto (10*1.23=12.30, 220*1.23=270.60, 350*1.23=430.50) — do wyświetlenia "Do zapłaty: X zł brutto". */
const PLAN_GROSS = {
  starter: 12.3,
  biznes: 270.6,
  premium: 430.5,
} as const;

const DEFAULT_INVENTORY = {
  __typename: 'Inventory' as const,
  offerPublish: {
    __typename: 'Product' as const,
    id: PRODUCT_IDS.starter,
    sku: PLAN_SKUS.starter,
    gross_amount: PLAN_GROSS.starter,
    description: 'Publikacja oferty na 30 dni, badge „Nowa" 3 dni. ',
    name: 'Starter',
    price: {
      __typename: 'Money' as const,
      currency: 'PLN',
      amount: 10,
    },
  },
  offerPublishBiznes: {
    __typename: 'Product' as const,
    id: PRODUCT_IDS.biznes,
    sku: PLAN_SKUS.biznes,
    gross_amount: PLAN_GROSS.biznes,
    description: 'Publikacja na 30 dni, podbicie na górę listy 15 dni. ',
    name: 'Biznes',
    price: {
      __typename: 'Money' as const,
      currency: 'PLN',
      amount: 220,
    },
  },
  offerPublishPremium: {
    __typename: 'Product' as const,
    id: PRODUCT_IDS.premium,
    sku: PLAN_SKUS.premium,
    gross_amount: PLAN_GROSS.premium,
    description: 'Publikacja na 30 dni, wyróżnienie „Pilna" 15 dni. ',
    name: 'Premium',
    price: {
      __typename: 'Money' as const,
      currency: 'PLN',
      amount: 350,
    },
  },
};

/** All 3 plans as array for plan selector (Starter, Biznes, Premium) */
export const PLANS_INVENTORY = [
  DEFAULT_INVENTORY.offerPublish,
  DEFAULT_INVENTORY.offerPublishBiznes,
  DEFAULT_INVENTORY.offerPublishPremium,
];

/**
 * Maps Polish attribute slugs (from DB) → OfferAttributeEnum values (used by
 * getAttrByType, STATIC_ATTRIBUTES, etc.). Without this the form breaks because
 * slug.toUpperCase() gives "PRAWO-JAZDY" while the enum expects "DRIVER_LICENSE".
 */
const SLUG_TO_ATTR_ENUM: Record<string, OfferAttributeEnum> = {
  'prawo-jazdy': OfferAttributeEnum.DRIVER_LICENSE,
  kierowca: OfferAttributeEnum.DRIVER,
  transport: OfferAttributeEnum.TRANSPORT,
  specyfikacja: OfferAttributeEnum.SPECIFICATION,
  doswiadczenie: OfferAttributeEnum.EXPERIENCE,
  lokalizacja: OfferAttributeEnum.LOCALIZATION,
  zatrudnienie: OfferAttributeEnum.EMPLOYMENT,
  korzysci: OfferAttributeEnum.BENEFITS,
  'rozmiar-firmy': OfferAttributeEnum.COMPANY_SIZE,
  tabor: OfferAttributeEnum.FLEET,
  'kierunki-transportu': OfferAttributeEnum.TRANSPORT_DIRECTIONS,
  'system-pracy': OfferAttributeEnum.WORK_SYSTEM,
  'typ-pojazdu': OfferAttributeEnum.VEHICLE_TYPE,
  operacja: OfferAttributeEnum.OPERATION,
  nadwozie: OfferAttributeEnum.BODY_TYPE,
  ladunek: OfferAttributeEnum.CARGO_TYPE,
  uprawnienia: OfferAttributeEnum.PERMISSIONS,
  zaloga: OfferAttributeEnum.CREW_TYPE,
  jezyki: OfferAttributeEnum.LANGUAGES,
};

/**
 * Transform REST Attribute to GraphQL Site_site_attributes format
 */
const transformAttribute = (attr: Attribute): Site_site['attributes'][number] => ({
  __typename: 'Attribute',
  type: SLUG_TO_ATTR_ENUM[attr.slug] || OfferAttributeEnum.CUSTOM,
  name: attr.name,
  id: btoa(`Attribute:${attr.id}`),
  slug: attr.slug,
  multi: attr.multi,
  choices: (Array.isArray(attr.choices) ? attr.choices : []).map((choice) => ({
    __typename: 'OfferAttributeChoice' as const,
    value: choice.value,
    id: btoa(`OfferAttributeChoice:${choice.id}`),
    slug: choice.slug,
  })),
});

/**
 * Filter type for offerFilters
 */
interface FilterType {
  __typename: 'Filter';
  type: OfferFiltersEnum;
  name: string;
  slug: string;
  choices: {
    __typename: 'OfferAttributeChoice';
    value: string;
    id: string;
    slug: string;
  }[];
}

/**
 * Transform REST Attribute to GraphQL Filter format for offerFilters
 */
const transformToFilter = (attr: Attribute, type: OfferFiltersEnum): FilterType => ({
  __typename: 'Filter',
  type,
  name: attr.name,
  slug: attr.slug,
  choices: (Array.isArray(attr.choices) ? attr.choices : []).map((choice) => ({
    __typename: 'OfferAttributeChoice' as const,
    value: choice.value,
    id: btoa(`OfferAttributeChoice:${choice.id}`),
    slug: choice.slug,
  })),
});

/**
 * Create empty filter for missing attribute types
 */
const createEmptyFilter = (name: string, slug: string, type: OfferFiltersEnum): FilterType => ({
  __typename: 'Filter',
  type,
  name,
  slug,
  choices: [],
});

/**
 * Build offerFilters from attributes.
 *
 * V2 mapping (6 slots — same grid, same components):
 *   1. Zatrudnienie      (slug: zatrudnienie)    — replaces legacy "Prawo jazdy"
 *   2. Zasięg            (slug: kierowca)        — renamed from "Kierowca" in DB
 *   3. Typ pojazdu       (slug: typ-pojazdu)     — replaces "Transport"
 *   4. System Pracy      (slug: system-pracy)    — replaces "Specyfikacja"
 *   5. Doświadczenie     (slug: doswiadczenie)   — unchanged
 *   6. Lokalizacja       (slug: lokalizacja)     — unchanged
 *
 * The property names (transport, specification) stay as-is in the TS interface
 * to avoid touching generated types. FilterSelect only reads filter.slug / .name.
 */
const buildOfferFilters = (attributes: Attribute[]): Site_site['offerFilters'] => {
  const attrMap = new Map(attributes.map((a) => [a.slug, a]));

  return {
    __typename: 'OfferFilters',
    driverLicense: attrMap.has('zatrudnienie')
      ? transformToFilter(attrMap.get('zatrudnienie')!, OfferFiltersEnum.EMPLOYMENT)
      : createEmptyFilter('Zatrudnienie', 'zatrudnienie', OfferFiltersEnum.EMPLOYMENT),
    driver: attrMap.has('kierowca')
      ? transformToFilter(attrMap.get('kierowca')!, OfferFiltersEnum.DRIVER)
      : createEmptyFilter('Zasięg', 'kierowca', OfferFiltersEnum.DRIVER),
    transport: attrMap.has('typ-pojazdu')
      ? transformToFilter(attrMap.get('typ-pojazdu')!, OfferFiltersEnum.VEHICLE_TYPE)
      : createEmptyFilter('Typ pojazdu', 'typ-pojazdu', OfferFiltersEnum.VEHICLE_TYPE),
    specification: attrMap.has('system-pracy')
      ? transformToFilter(attrMap.get('system-pracy')!, OfferFiltersEnum.WORK_SYSTEM)
      : createEmptyFilter('System Pracy', 'system-pracy', OfferFiltersEnum.WORK_SYSTEM),
    experience: attrMap.has('doswiadczenie')
      ? transformToFilter(attrMap.get('doswiadczenie')!, OfferFiltersEnum.EXPERIENCE)
      : createEmptyFilter('Doświadczenie', 'doswiadczenie', OfferFiltersEnum.EXPERIENCE),
    localization: attrMap.has('lokalizacja')
      ? transformToFilter(attrMap.get('lokalizacja')!, OfferFiltersEnum.LOCALIZATION)
      : createEmptyFilter('Lokalizacja', 'lokalizacja', OfferFiltersEnum.LOCALIZATION),
  } as Site_site['offerFilters'];
};

/**
 * REST adapter for useSiteQuery
 * Combines attributes and site config to build full Site response
 */
export const useSiteQuery = () => {
  const {
    data: attributes,
    isLoading: attributesLoading,
    error: attributesError,
  } = useAttributes();

  const { data: siteConfig, isLoading: configLoading, error: configError } = useSiteConfig();

  const loading = attributesLoading || configLoading;
  const error = attributesError || configError;

  const transformedData: Site | undefined = useMemo(() => {
    if (!attributes) return undefined;
    // Normalize: API może zwrócić tablicę lub obiekt z polem results (paginacja)
    const list = Array.isArray(attributes)
      ? attributes
      : typeof attributes === 'object' && Array.isArray((attributes as { results?: Attribute[] }).results)
        ? (attributes as { results: Attribute[] }).results
        : [];

    const site: Site_site = {
      __typename: 'Site',
      seo: DEFAULT_SEO,
      homePageTitle: 'Oferty pracy dla kierowców',
      locale: 'pl',
      logoUploadSettings: DEFAULT_LOGO_SETTINGS,
      companyDescriptionMaxLen: 2000,
      offerDescriptionMaxLen: 5000,
      portalName: 'Praca na kołach',
      applicationSettings: DEFAULT_APPLICATION_SETTINGS,
      offerFilters: list.length === 0 ? buildOfferFilters([]) : buildOfferFilters(list),
      attributes: list.map(transformAttribute),
      offerSettings: DEFAULT_OFFER_SETTINGS,
      inventory: DEFAULT_INVENTORY,
      bankTransferInfo: null,
    };

    return { site };
  }, [attributes, siteConfig]);

  return {
    data: transformedData,
    loading,
    error: error ? new Error(String(error)) : undefined,
  };
};
